import { useCallback } from 'react'
import { ApolloError, useMutation } from '@apollo/client'
import { AdminAPI, utils } from '@guestgoodie/graphql'

import * as Form from '@/features/form'
import { useLingui } from '@lingui/react/macro'

export type FormData = {
  identifier: string
  name: string
  description: string
}

interface Options {
  id?: number
  onDone?: () => void
}

export default function useSpaceForm(options: Options) {
  const { id, onDone } = options

  const { t } = useLingui()

  const [createSpace] = useMutation(AdminAPI.space.CREATE, {
    refetchQueries: [AdminAPI.space.QUERY]
  })

  const [updateSpace] = useMutation(AdminAPI.space.UPDATE, {
    refetchQueries: [
      {
        query: AdminAPI.space.GET,
        variables: {
          id
        }
      }
    ]
  })

  const [deleteSpace] = useMutation(AdminAPI.space.DELETE, {
    update(cache, { data }) {
      if (!data) return

      utils.paginationHelpers.remove(
        cache,
        data.deleteSpace.id,
        'Space',
        'querySpaces'
      )
    }
  })

  const { parseApolloErrors, errors, setServerError } =
    Form.useErrorHelpers<FormData>({
      mapper(field, message) {
        if (field === 'identifier' && message === 'has already been taken') {
          return [
            field,
            {
              type: message,
              message: t`This identifier exists already.`
            }
          ]
        }
      }
    })

  const create = useCallback(
    async (data: FormData) => {
      try {
        const response = await createSpace({
          variables: {
            input: data
          }
        })

        if (response.data) onDone?.()
        if (response.errors) parseApolloErrors(response.errors)
      } catch (error) {
        if (error instanceof ApolloError) {
          if (error.message === 'not_enough_licenses') {
            setServerError(
              'error-not-enough-licenses',
              t({
                comment:
                  'Error message shown when a user tries to create a room, even though it would succeed their license.',
                message:
                  'This Room would exceed your current licenses, please update your licenses before continuing.'
              })
            )

            return
          }

          return parseApolloErrors(error.graphQLErrors)
        }

        // re-throw so we catch it somewhere else
        throw error
      }
    },
    [onDone, parseApolloErrors, createSpace, setServerError, t]
  )

  const update = useCallback(
    async (data: FormData) => {
      if (!id) return

      try {
        const response = await updateSpace({
          variables: {
            input: {
              id,
              ...data
            }
          }
        })

        if (response.data) onDone?.()
        if (response.errors) parseApolloErrors(response.errors)
      } catch (error) {
        if (error instanceof ApolloError) {
          return parseApolloErrors(error.graphQLErrors)
        }

        // re-throw so we catch it somewhere else
        throw error
      }
    },
    [id, onDone, updateSpace, parseApolloErrors]
  )

  const remove = useCallback(() => {
    if (!id) return

    deleteSpace({ variables: { id } })
    onDone?.()
  }, [id, onDone, deleteSpace])

  return {
    errors,
    create,
    update,
    remove
  }
}
